// @ts-nocheck
import * as React from "react";
import { Ic, Sidebar, Topbar, AppFooter, useCurrentUser, apiErrorMessage } from "./common";
import {
  CaseSummaryPane, CaseMedsPane, CaseVisitsPane, CaseReportsPane, CaseFamilyPane,
} from "./mobile-manager";
import { usePermissionsFromSession } from "../components/permissions";
import { servicePlans as spApi } from "../api";

// 매니저용 데스크톱 케이스 상세 (/case?elderly_id=N).
//
// 모바일 MgrCase 와 같은 데이터·탭 구조를 유지하되, 데스크톱 chrome 에서
// 자연스러운 2-col 레이아웃으로 재배치한다. /m/reports 등 데스크톱 화면에서
// "이 어르신 케이스 →" 진입 시 모바일 프레임으로 점프하던 어색함을 제거.
//
// 패널 컴포넌트(요약/복약/일정/기록/가족)는 모바일 본문과 1:1 동일 — 같은
// 컴포넌트를 그대로 import 해 재사용한다. 정보의 의미와 표시는 화면 크기에
// 무관하므로 패널을 분기하지 않고, 둘러싸는 chrome (사이드바·프로필 카드·
// 탭 네비)만 데스크톱용으로 갈아낀다.
//
// 데이터: 모바일과 동일하게 GET /api/v1/mobile/case/:elderly_id.
// 권한 분기: 운영자도 접근 가능하지만 사이드바는 매니저용으로 자동 표시될 것.
// 매니저가 아닌 경우 본인 담당 외 어르신을 보려고 진입한 시나리오는 백엔드가
// 권한에 따라 다른 결과를 줄 수 있으므로 여기선 추가 가드를 두지 않는다.

const RISK_LABEL  = { critical: "위험", warning: "주의", stable: "안정" };
const RISK_COLOR  = { critical: "rose", warning: "amber", stable: "green" };
const HH_TYPE_LABEL = {
  single:  "1인가구",
  couple:  "노부부",
  with_family: "가족동거",
  other:   "기타",
};

const CASE_TABS = [
  { key: "summary", label: "요약" },
  { key: "plan",    label: "계획" },
  { key: "meds",    label: "복약" },
  { key: "visits",  label: "일정" },
  { key: "reports", label: "기록" },
  { key: "family",  label: "가족" },
];

async function jsonFetch(url, opts = {}) {
  const res = await fetch(url, { credentials: "same-origin", ...opts });
  let body = null;
  try { body = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) {
    const err = new Error(body?.message || body?.error || `HTTP ${res.status}`);
    err.status = res.status; err.body = body;
    err.code = body?.error;
    throw err;
  }
  return body;
}
function humanError(e) {
  if (!e) return "처리에 실패했습니다.";
  if (e.status === 401) return "로그인이 필요합니다.";
  if (e.status === 403) return "이 어르신 케이스에 대한 권한이 없습니다.";
  if (e.status === 404) return "해당 어르신을 찾을 수 없습니다.";
  return apiErrorMessage(e);
}

// ─────────────────────────────────────────────────────────────
// 라우팅 wrapper — useCurrentUser 만 호출하는 안정 hook 셋.
// ─────────────────────────────────────────────────────────────
function MgrCaseDesktop() {
  const { user, loaded } = useCurrentUser();
  if (!loaded) return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;
  return <MgrCaseDesktopInner user={user}/>;
}

// 역할별 chrome 분기 — 사이드바 highlight 와 "돌아가기" 진입점이 사용자의 메뉴에
// 실제로 존재하는 항목만 가리키도록. 매니저는 본인 담당 어르신 목록(/cases)으로,
// 운영자는 권역 대시보드로 회귀하도록 분기한다. 매니저 사이드바엔 'cases' 키가
// 있고 운영자 사이드바엔 없어서, 운영자에게 'cases'를 active 로 넘기면 아무
// 항목도 highlight 되지 않는 빈 상태가 된다 — 그 자리에 dashboard 를 highlight.
function chromeForRole(role) {
  switch (role) {
    case "manager":
      return { activeKey: "cases", backHref: "/cases", backLabel: "‹ 담당 어르신 목록" };
    case "region_admin":
    case "agency":
      return { activeKey: "dashboard", backHref: "/dashboard/region", backLabel: "‹ 권역 대시보드" };
    case "system_admin":
      return { activeKey: "dashboard", backHref: "/dashboard/platform", backLabel: "‹ 플랫폼 대시보드" };
    default:
      // 비로그인·예외 역할 — highlight 없이 매니저 기본값을 fallback 으로.
      return { activeKey: "cases", backHref: "/cases", backLabel: "‹ 목록으로" };
  }
}

function MgrCaseDesktopInner({ user }) {
  const [data,    setData]    = React.useState(null);
  const [err,     setErr]     = React.useState(null);
  const [activeTab, setActiveTab] = React.useState("summary");

  const chrome = chromeForRole(user?.role);

  const elderlyId = React.useMemo(() => {
    const qs = new URLSearchParams(window.location.search);
    return qs.get("elderly_id") || qs.get("id") || null;
  }, []);

  const load = React.useCallback(async () => {
    setErr(null);
    try {
      const url = elderlyId ? `/api/v1/mobile/case/${encodeURIComponent(elderlyId)}` : "/api/v1/mobile/case";
      const body = await jsonFetch(url);
      setData(body);
    } catch (e) { setErr(humanError(e)); }
  }, [elderlyId]);
  React.useEffect(() => { load(); }, [load]);

  // 케이스 메모 저장 — 응답 case 직렬화로 로컬을 덮어 다른 탭에서 돌아와도 최신값.
  const saveNote = React.useCallback(async (next) => {
    const caseId = data?.case?.id;
    if (!caseId) throw new Error("케이스가 없습니다.");
    const updated = await jsonFetch(`/api/v1/cases/${caseId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ case: { note: next } }),
    });
    setData(d => d && { ...d, case: { ...d.case, note: updated.note } });
  }, [data?.case?.id]);

  // 케이스 상태 저장 — 종결 시 closure_reason 동봉.
  const saveStatus = React.useCallback(async ({ status, closure_reason }) => {
    const caseId = data?.case?.id;
    if (!caseId) throw new Error("케이스가 없습니다.");
    const body = { case: { status } };
    if (closure_reason) body.case.closure_reason = closure_reason;
    const updated = await jsonFetch(`/api/v1/cases/${caseId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    setData(d => d && {
      ...d,
      case: { ...d.case, status: updated.status, closure_reason: updated.closure_reason, closed_at: updated.closed_at },
    });
  }, [data?.case?.id]);

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar active={chrome.activeKey}/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="케이스 상세" sub={data ? `${data.elderly?.name || ''} 어르신` : ""}/>
          <div className="main">

            {err && (
              <div className="card" style={{padding:16, color:'var(--rose-500)', background:'var(--rose-50)', marginBottom:14}}>
                {err}
                <div style={{marginTop:10}}>
                  <a href={chrome.backHref} className="btn btn-ghost btn-sm" style={{textDecoration:'none'}}>{chrome.backLabel}</a>
                </div>
              </div>
            )}

            {!err && !data && (
              <div className="muted" style={{padding:40, textAlign:'center'}}>불러오는 중…</div>
            )}

            {!err && data && (
              <div style={{display:'grid', gridTemplateColumns:'320px 1fr', gap:16}}>
                <ProfileCard data={data} chrome={chrome}/>
                <TabContainer activeTab={activeTab} setActiveTab={setActiveTab} data={data}
                              saveNote={saveNote} saveStatus={saveStatus}/>
              </div>
            )}

          </div>
          <AppFooter/>
        </div>
      </div>
    </div>
  );
}

function ProfileCard({ data, chrome }) {
  const e = data.elderly || {};
  const g = {
    name:     e.guardian_name,
    phone:    e.guardian_phone,
    relation: e.guardian_relation,
  };
  return (
    <div style={{display:'flex', flexDirection:'column', gap:12, position:'sticky', top:0}}>
      <div className="card" style={{padding:'18px 18px'}}>
        <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:6, paddingBottom:14, borderBottom:'1px solid var(--border-soft)'}}>
          <div className="avatar lg" style={{width:72, height:72, fontSize:26}}>{(e.name || '·')[0]}</div>
          <div style={{fontSize:18, fontWeight:800, marginTop:8}}>
            {e.name} <span style={{fontWeight:500, color:'var(--ink-3)', fontSize:13}}>· {e.age}세</span>
          </div>
          <div style={{display:'flex', gap:5, flexWrap:'wrap', justifyContent:'center'}}>
            {e.risk_level && <span className={"badge badge-" + RISK_COLOR[e.risk_level]}>{RISK_LABEL[e.risk_level]}</span>}
            {e.household_type && <span className="badge badge-gray">{HH_TYPE_LABEL[e.household_type] || e.household_type}</span>}
          </div>
        </div>

        <div style={{display:'grid', gridTemplateColumns:'70px 1fr', gap:'8px 10px', fontSize:12, padding:'12px 0'}}>
          <div className="muted">주소</div>
          <div>{e.address || '-'}</div>
          {e.phone && (<>
            <div className="muted">연락처</div>
            <div className="mono">{e.phone}</div>
          </>)}
          {e.health_status && (<>
            <div className="muted">건강상태</div>
            <div>{e.health_status}</div>
          </>)}
        </div>

        {(g.name || g.phone) && (
          <div style={{borderTop:'1px solid var(--border-soft)', paddingTop:12}}>
            <div className="muted" style={{fontSize:10, fontWeight:700, letterSpacing:'0.06em', marginBottom:8}}>보호자</div>
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:10}}>
              <div className="avatar" style={{width:34, height:34, fontSize:12, background:'var(--orange-50)', color:'var(--orange-700)'}}>
                {(g.name || '?')[0]}
              </div>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:13, fontWeight:700}}>{g.name || '미등록'}</div>
                <div className="muted" style={{fontSize:11, marginTop:1}}>
                  {g.relation || '관계 미상'}
                  {g.phone ? ` · ${g.phone}` : ''}
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{display:'flex', flexDirection:'column', gap:8, paddingTop:6}}>
          {g.phone ? (
            <a href={`tel:${g.phone}`} className="btn btn-ghost btn-md"
               aria-label={`보호자 ${g.name || ''} 전화`}
               style={{textDecoration:'none', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8}}>
              <span style={{width:18, height:18, display:'inline-block'}}>{Ic.phone}</span>
              보호자 전화
            </a>
          ) : (
            <button type="button" disabled className="btn btn-ghost btn-md"
                    title="보호자 전화번호 미등록"
                    style={{opacity:0.5, cursor:'not-allowed', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8}}>
              <span style={{width:18, height:18, display:'inline-block'}}>{Ic.phone}</span>
              보호자 전화 (미등록)
            </button>
          )}
          <a href={`/schedule${data.upcoming_visits?.[0] ? `?date=${(data.upcoming_visits[0].scheduled_at || '').slice(0,10)}` : ''}`}
             className="btn btn-primary btn-md"
             style={{textDecoration:'none', display:'inline-flex', alignItems:'center', justifyContent:'center'}}>
            일정에서 방문 시작 →
          </a>
          <a href={chrome.backHref} className="btn btn-ghost btn-sm"
             style={{textDecoration:'none', display:'inline-flex', alignItems:'center', justifyContent:'center'}}>
            {chrome.backLabel}
          </a>
        </div>
      </div>
    </div>
  );
}

function TabContainer({ activeTab, setActiveTab, data, saveNote, saveStatus }) {
  const e = data.elderly || {};
  const v = (data.recent_vitals && data.recent_vitals[0]) || null;
  return (
    <div className="card" style={{padding:0, overflow:'hidden'}}>
      <div style={{display:'flex', borderBottom:'1px solid var(--border-soft)', overflowX:'auto'}}>
        {CASE_TABS.map((t) => {
          const active = activeTab === t.key;
          return (
            <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
              style={{padding:'14px 22px', fontSize:13, fontWeight: active ? 700 : 500,
                      color: active ? 'var(--orange-700)' : 'var(--ink-3)',
                      borderBottom: active ? '2px solid var(--orange-500)' : '2px solid transparent',
                      background:'transparent', border:'none', borderRadius:0, cursor:'pointer', whiteSpace:'nowrap'}}>
              {t.label}
            </button>
          );
        })}
      </div>

      <div style={{padding:'18px 22px'}}>
        {activeTab === "summary" && (
          <CaseSummaryPane elderly={e} latestVital={v}
                           weeklyVisitCount={data.this_week_visit_count ?? 0}
                           caseRecord={data.case}
                           onSaveNote={saveNote} onSaveStatus={saveStatus}
                           recentVitals={data.recent_vitals}/>
        )}
        {activeTab === "plan"    && <CasePlanPane caseRecord={data.case}/>}
        {activeTab === "meds"    && <CaseMedsPane medications={e.medications}/>}
        {activeTab === "visits"  && <CaseVisitsPane upcoming={data.upcoming_visits} past={data.past_visits}/>}
        {activeTab === "reports" && <CaseReportsPane reports={data.recent_vitals}/>}
        {activeTab === "family"  && <CaseFamilyPane elderly={e}/>}
      </div>
    </div>
  );
}

// 통합돌봄 서비스계획 패널 — 케이스별 계획 이력 + 활성 계획의 항목 표시.
// 운영자(can_manage_service_plans) 만 편집 가능; 매니저는 조회만.
function CasePlanPane({ caseRecord }) {
  const { user, loaded } = useCurrentUser();
  const perms = usePermissionsFromSession({ user, loaded });
  const [plans, setPlans] = React.useState(null);
  const [err, setErr]     = React.useState(null);
  const [editing, setEditing] = React.useState(null); // null | 'new' | plan object

  const caseId = caseRecord?.id;
  const reload = React.useCallback(() => {
    if (!caseId) { setPlans([]); return; }
    spApi.listForCase(caseId).then(setPlans).catch(e => setErr(apiErrorMessage(e)));
  }, [caseId]);
  React.useEffect(reload, [reload]);

  if (!caseId) return <div className="muted" style={{padding:20}}>케이스가 등록되기 전에는 계획을 작성할 수 없습니다.</div>;
  if (err) return <div style={{color:'var(--rose-500)', padding:20}}>{err}</div>;
  if (plans === null) return <div className="muted" style={{padding:20}}>불러오는 중…</div>;

  const active = plans.find(p => p.status === 'approved');
  const drafts = plans.filter(p => p.status !== 'approved');

  return (
    <div>
      <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:14}}>
        <div className="h3" style={{fontSize:15}}>서비스계획</div>
        <div className="muted" style={{fontSize:12, flex:1}}>
          케이스당 활성 계획 1개 · 항목은 서비스 카탈로그(LTC/NLT/ER) 코드 기준
        </div>
        {perms.canManageServicePlans && (
          <button className="btn btn-primary btn-sm" onClick={()=>setEditing('new')}>+ 새 계획</button>
        )}
      </div>

      {plans.length === 0 && (
        <div className="muted" style={{padding:20, textAlign:'center'}}>
          아직 서비스계획이 없습니다. {perms.canManageServicePlans && '"새 계획"으로 작성해주세요.'}
        </div>
      )}

      {active && <PlanCard plan={active} active onOpen={()=>setEditing(active)} onReload={reload}/>}

      {drafts.length > 0 && (
        <div style={{marginTop:14}}>
          <div className="muted" style={{fontSize:11, fontWeight:700, letterSpacing:'0.04em', marginBottom:8}}>초안·이력</div>
          <div style={{display:'flex', flexDirection:'column', gap:8}}>
            {drafts.map(p => <PlanCard key={p.id} plan={p} active={false} onOpen={()=>setEditing(p)} onReload={reload}/>)}
          </div>
        </div>
      )}

      {editing && (
        <ServicePlanModal
          caseId={caseId}
          initial={editing === 'new' ? null : editing}
          canEdit={perms.canManageServicePlans}
          onClose={()=>setEditing(null)}
          onSaved={()=>{ setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function PlanCard({ plan, active, onOpen, onReload }) {
  const status = plan.status === 'approved' ? { c:'green', l:'활성' } : { c:'gray', l:'초안' };
  const period = [plan.starts_on, plan.ends_on].filter(Boolean).map(d => new Date(d).toLocaleDateString('ko-KR'));
  const periodLabel = period.length === 0 ? '기간 미정' : period.length === 1 ? `${period[0]} ~` : `${period[0]} ~ ${period[1]}`;
  return (
    <div onClick={onOpen}
      style={{padding:'12px 14px', borderRadius:10,
        background: active ? 'var(--orange-50)' : 'var(--bg-sub)',
        borderLeft: `4px solid var(--${status.c}-500)`, cursor:'pointer'}}>
      <div style={{display:'flex', alignItems:'center', gap:8}}>
        <span className={"badge badge-" + status.c}>{status.l}</span>
        <div style={{fontSize:13, fontWeight:700, flex:1}}>계획 #{plan.id} · 항목 {plan.items_count}개</div>
        <div className="muted" style={{fontSize:11}}>{periodLabel}</div>
      </div>
      {plan.author && (
        <div className="muted" style={{fontSize:11, marginTop:3}}>작성 {plan.author.name}</div>
      )}
    </div>
  );
}

// 서비스계획 편집 모달 — items 를 nested 로 저장. 항목 추가/삭제/편집 지원.
// 승인된 계획은 서버가 편집을 잠그므로(controller locked 응답) 읽기전용.
function ServicePlanModal({ caseId, initial, canEdit, onClose, onSaved }) {
  const readOnly = !canEdit || initial?.status === 'approved';
  const [plan, setPlan] = React.useState(initial); // 상세 조회 후 items 채워짐
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [catalog, setCatalog] = React.useState({});

  // 상세 조회 — 최초 진입 시 items 포함해 다시 로드.
  React.useEffect(() => {
    if (!initial?.id) return;
    spApi.get(initial.id).then(setPlan).catch(e => setErr(apiErrorMessage(e)));
  }, [initial?.id]);

  // 서비스 카탈로그 로드 (LTC/NLT/ER 코드 · 라벨). settings API 가 없다면 세션 안 카탈로그 참조.
  React.useEffect(() => {
    fetch("/api/v1/settings/care_service_types.json", { credentials:"same-origin" })
      .then(r => r.ok ? r.json() : {})
      .then(j => {
        // 응답이 없으면 하드코딩 기본 폴백. (Setting.care_service_labels 동일)
        const flat = {};
        if (j && typeof j === 'object' && Object.keys(j).length) {
          for (const k in j) for (const it of (j[k] || [])) flat[it.code] = it.label;
        } else {
          // 최소 폴백 — 실제 값은 서버 응답이 우선.
          Object.assign(flat, {
            'LTC-01':'방문요양','LTC-02':'방문목욕','LTC-03':'방문간호','LTC-04':'주야간보호',
            'LTC-05':'단기보호','LTC-06':'복지용구','LTC-07':'시설급여',
            'NLT-01':'가사지원','NLT-02':'이동지원','NLT-03':'정서지원','NLT-04':'방문의료',
            'NLT-05':'응급안전','NLT-06':'식사배달',
            'ER-01':'응급실 이송','ER-02':'퇴원 후 연계','ER-03':'119 협력 안전확인',
          });
        }
        setCatalog(flat);
      })
      .catch(()=>{});
  }, []);

  const [items, setItems] = React.useState(() => initial?.items || []);
  const [notes, setNotes] = React.useState(initial?.notes || '');
  const [starts, setStarts] = React.useState(initial?.starts_on || '');
  const [ends,   setEnds]   = React.useState(initial?.ends_on || '');

  // 상세 로드가 완료되면 폼 초기값 갱신.
  React.useEffect(() => {
    if (!plan) return;
    setItems(plan.items || []);
    setNotes(plan.notes || '');
    setStarts(plan.starts_on || '');
    setEnds(plan.ends_on || '');
  }, [plan]);

  const codeOptions = Object.entries(catalog);

  const addItem = () => {
    setItems([...items, { _new: true, service_code: codeOptions[0]?.[0] || 'LTC-01',
      frequency_per_week: 1, duration_mins: 60, priority: 3, notes: '' }]);
  };
  const updateItem = (idx, patch) => setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const removeItem = (idx) => setItems(items.map((it, i) => i === idx ? { ...it, _destroy: true } : it));
  const visible = items.filter(it => !it._destroy);

  const save = async (approve) => {
    setSaving(true); setErr(null);
    try {
      const payload = {
        starts_on: starts || null, ends_on: ends || null, notes,
        items_attributes: items.map(it => {
          const base = { service_code: it.service_code, frequency_per_week: it.frequency_per_week,
            duration_mins: it.duration_mins, priority: it.priority, notes: it.notes };
          if (it.id) base.id = it.id;
          if (it._destroy) base._destroy = true;
          return base;
        }),
      };
      let saved;
      if (initial?.id) {
        saved = await spApi.update(initial.id, payload);
        if (approve) saved = await spApi.approve(initial.id);
      } else {
        saved = await spApi.create(caseId, payload);
        if (approve) saved = await spApi.approve(saved.id);
      }
      onSaved(saved);
    } catch (e) { setErr(apiErrorMessage(e)); setSaving(false); }
  };

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:60}}>
      <div onClick={e=>e.stopPropagation()} className="card"
        style={{width:760, maxHeight:'92vh', overflow:'auto', padding:0}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)',
          display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="h3">{initial?.id ? `계획 #${initial.id}` : '새 서비스계획'}</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>
              {readOnly ? '승인됨 · 편집 불가' : '항목별 서비스 코드·빈도·기간을 지정하고 승인하면 활성화됩니다.'}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>

        {err && (
          <div style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--rose-50)',
            color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>
        )}

        <div style={{padding:'18px 22px', display:'flex', flexDirection:'column', gap:14}}>
          {/* 기간 + 메모 */}
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 2fr', gap:12}}>
            <div>
              <div className="muted" style={{fontSize:10.5, fontWeight:700, marginBottom:4}}>시작일</div>
              <input type="date" className="input" value={starts||''}
                onChange={e=>setStarts(e.target.value)} disabled={readOnly} style={{width:'100%'}}/>
            </div>
            <div>
              <div className="muted" style={{fontSize:10.5, fontWeight:700, marginBottom:4}}>종료일(선택)</div>
              <input type="date" className="input" value={ends||''}
                onChange={e=>setEnds(e.target.value)} disabled={readOnly} style={{width:'100%'}}/>
            </div>
            <div>
              <div className="muted" style={{fontSize:10.5, fontWeight:700, marginBottom:4}}>메모</div>
              <input className="input" value={notes} onChange={e=>setNotes(e.target.value)}
                disabled={readOnly} placeholder="계획 총평·특이사항" style={{width:'100%'}}/>
            </div>
          </div>

          {/* 항목 편집 */}
          <div>
            <div style={{display:'flex', alignItems:'center', marginBottom:8}}>
              <div className="muted" style={{fontSize:10.5, fontWeight:700, letterSpacing:'0.04em', flex:1}}>
                서비스 항목 ({visible.length}개)
              </div>
              {!readOnly && (
                <button className="btn btn-ghost btn-sm" onClick={addItem}>+ 항목 추가</button>
              )}
            </div>
            {visible.length === 0 && (
              <div className="muted" style={{fontSize:12, padding:'20px 0', textAlign:'center'}}>
                항목이 없습니다.
              </div>
            )}
            <div style={{display:'flex', flexDirection:'column', gap:8}}>
              {items.map((it, idx) => {
                if (it._destroy) return null;
                return (
                  <div key={idx} style={{padding:'10px 12px', border:'1px solid var(--border-soft)',
                    borderRadius:8, display:'grid', gridTemplateColumns:'160px 90px 100px 80px 1fr auto',
                    gap:8, alignItems:'center'}}>
                    <select className="select" value={it.service_code}
                      disabled={readOnly}
                      onChange={e=>updateItem(idx, { service_code: e.target.value })}>
                      {codeOptions.map(([code, label]) => (
                        <option key={code} value={code}>{code} · {label}</option>
                      ))}
                    </select>
                    <input type="number" min="1" max="21" className="input" value={it.frequency_per_week||''}
                      disabled={readOnly} title="주당 회수"
                      onChange={e=>updateItem(idx, { frequency_per_week: Number(e.target.value)||null })}
                      placeholder="주N회"/>
                    <input type="number" min="1" max="480" step="15" className="input" value={it.duration_mins||''}
                      disabled={readOnly} title="회당 분"
                      onChange={e=>updateItem(idx, { duration_mins: Number(e.target.value)||null })}
                      placeholder="분"/>
                    <select className="select" value={it.priority||3}
                      disabled={readOnly} title="우선순위 1(최우선)~5(참고)"
                      onChange={e=>updateItem(idx, { priority: Number(e.target.value) })}>
                      {[1,2,3,4,5].map(p => <option key={p} value={p}>P{p}</option>)}
                    </select>
                    <input className="input" value={it.notes||''}
                      disabled={readOnly} placeholder="비고"
                      onChange={e=>updateItem(idx, { notes: e.target.value })}/>
                    {!readOnly && (
                      <button className="btn btn-ghost btn-sm"
                        onClick={()=>removeItem(idx)} title="삭제">✕</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)',
          display:'flex', justifyContent:'flex-end', gap:8}}>
          <button className="btn btn-ghost btn-md" onClick={onClose} disabled={saving}>닫기</button>
          {!readOnly && (
            <>
              <button className="btn btn-ghost btn-md" onClick={()=>save(false)} disabled={saving}>
                {saving ? '저장 중…' : '초안 저장'}
              </button>
              <button className="btn btn-primary btn-md" onClick={()=>save(true)} disabled={saving || visible.length === 0}>
                {saving ? '저장 중…' : '저장·승인'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export { MgrCaseDesktop };
